import React, { useState } from 'react';

import ChevronLeft from '@/icons/light/chevron-left.svg';
import ChevronRight from '@/icons/light/chevron-right.svg';

import Grid from '../grid';

import type { FunctionComponent } from 'react';
import 'moment/locale/es';
import type {
  TypeCalendarMonthsProps,
  TypeColorData,
  TypeDatesData,
} from './types';

const MONTHS = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/**
 * CalendarMonths
 */
export const CalendarMonths: FunctionComponent<TypeCalendarMonthsProps> = ({
  className = '',
  availableDates,
}: TypeCalendarMonthsProps) => {
  const now = new Date();

  const [year, setYear] = useState<number>(now.getFullYear());

  const dates: TypeDatesData[] = [];

  const colorData = new Map<string, TypeColorData>();

  const lastYear = new Date(
    availableDates[availableDates.length - 1].hasta * 1000
  ).getFullYear();

  //TODO: Mover este calculo al back ya que solo se calcula cada 8 horas?, no tiene sentido tenerlo aqui que se calcula en cada render

  // Iterate over all available dates and create a map with all the data for every month
  availableDates.forEach((d) => {
    const desde = new Date(d.desde * 1000);
    const hasta = new Date(d.hasta * 1000);

    // TODO: Improve this or delete ir before move it to the back
    // We can skip calculations for other years that aren´t on screen.
    if (desde.getFullYear() !== year && hasta.getFullYear() !== year) {
      return;
    }

    // tempDesde and tempHasta are used as acumulators in case a date ends in one month and the next one starts in the same month
    let tempDesde: TypeColorData | undefined = [];
    let tempHasta: TypeColorData | undefined = [];

    if (colorData.has(`${desde.getFullYear()}-${desde.getMonth()}`)) {
      tempDesde = colorData.get(`${desde.getFullYear()}-${desde.getMonth()}`);
    }

    if (colorData.has(`${hasta.getFullYear()}-${hasta.getMonth()}`)) {
      tempHasta = colorData.get(`${hasta.getFullYear()}-${hasta.getMonth()}`);
    }

    if (
      desde.getMonth() === hasta.getMonth() &&
      desde.getFullYear() === hasta.getFullYear()
    ) {
      if (!tempDesde) {
        throw Error('Dates not found');
      }

      tempDesde.push({
        from: desde.getDate(),
        to: hasta.getDate(),
        porcentajeInit: desde.getDate() === 1 ? 0 : desde.getDate() * 3.33,
        porcentajeEnd: hasta.getDate() * 3.33,
      });

      colorData.set(`${desde.getFullYear()}-${desde.getMonth()}`, tempDesde);
    } else {
      if (!tempDesde || !tempHasta) {
        throw Error('Dates not found');
      }

      tempDesde.push({
        from: desde.getDate(),
        to: 30,
        porcentajeInit: desde.getDate() * 3.33,
        porcentajeEnd: 100,
      });

      colorData.set(`${desde.getFullYear()}-${desde.getMonth()}`, tempDesde);

      const monthDiff =
        hasta.getMonth() -
        desde.getMonth() +
        12 * (hasta.getFullYear() - desde.getFullYear());

      if (monthDiff > 1) {
        const date = new Date(desde);

        for (let i = 1; i < monthDiff; ++i) {
          date.setMonth(date.getMonth() + 1);

          colorData.set(`${date.getFullYear()}-${date.getMonth()}`, [
            {
              from: 1,
              to: 30,
              porcentajeInit: 0,
              porcentajeEnd: 100,
            },
          ]);
        }
      }

      tempHasta.push({
        from: 1,
        to: hasta.getDate(),
        porcentajeInit: 0,
        porcentajeEnd: hasta.getDate() === 1 ? 0 : hasta.getDate() * 3.33,
      });

      colorData.set(`${hasta.getFullYear()}-${hasta.getMonth()}`, tempHasta);
    }
  });

  // Iterate over the colorData map and generate the css for every month
  const colorDataTransformed = new Map<string, string>();
  for (const [key, value] of colorData) {
    let colorData = '';
    value.forEach((v) => {
      const temp =
        v.from === 1
          ? `linear-gradient(90deg, #D9EBEF ${v.porcentajeInit}% ${v.porcentajeEnd}%, transparent ${v.porcentajeEnd}% )`
          : `linear-gradient(90deg, transparent 0% ${v.porcentajeInit}%,  #D9EBEF ${v.porcentajeInit}% ${v.porcentajeEnd}%, transparent ${v.porcentajeEnd}% )`;

      if (colorData !== '') {
        colorData = `${colorData}, ${temp}`;
      } else {
        colorData = temp;
      }
    });

    colorDataTransformed.set(key, `${colorData}, #F8C7C4`);
  }

  // Iterate over every month to add the css as well indicate if the month is disabled (past month from now)
  MONTHS.forEach((m, i) => {
    dates.push({
      disabled: now.getMonth() > i && now.getFullYear() === year ? true : false,
      month: m,
      colorData: colorDataTransformed.get(`${year}-${i}`),
    });
  });

  return (
    <>
      <div
        className={`flex max-w-[328px] flex-col gap-4 rounded-lg border border-neutrals-300 py-4 px-6 max-md:mx-auto ${className}`}
      >
        <div className="flex items-center gap-1">
          <button
            className="rounded-full border border-primary-100 p-2 text-primary-500 disabled:cursor-not-allowed disabled:border-primary-50 disabled:text-primary-50"
            type="button"
            disabled={now.getFullYear() === year}
            onClick={() => setYear((y) => --y)}
          >
            <ChevronLeft width={24} height={24} />
          </button>

          <p className="text-body text-body--l grow text-center font-bold">
            {year}
          </p>

          <button
            className="rounded-full border border-primary-100 p-2 text-primary-500 disabled:cursor-not-allowed disabled:border-primary-50 disabled:text-primary-50"
            type="button"
            disabled={lastYear === year}
            onClick={() => setYear((y) => ++y)}
          >
            <ChevronRight width={24} height={24} />
          </button>
        </div>

        <Grid className="max-w-[280px] grid-cols-3 gap-0.5">
          {dates.map((d, i) => (
            <Grid.Item
              className={`flex justify-center rounded-[2px] py-1.5 ${
                d.disabled ? '' : 'bg-[#F8C7C4]'
              }`}
              key={i}
              {...(d.colorData && {
                style: {
                  background: d.colorData,
                },
              })}
            >
              <p
                className={`text-button p-2.5 ${
                  d.disabled ? 'text-neutrals-300' : 'text-text-blue-900'
                }`}
              >
                {d.month}
              </p>
            </Grid.Item>
          ))}
        </Grid>
      </div>

      <div className="text-body text-body--m mt-4 flex gap-4">
        <div className="flex items-center justify-center gap-2">
          <span className="inline-block h-4 w-4 rounded-full bg-primary-100" />{' '}
          Disponible
        </div>

        <div className="flex items-center justify-center gap-2">
          <span className="inline-block h-4 w-4 rounded-full bg-[#F8C7C4]" />{' '}
          Ocupado
        </div>
      </div>
    </>
  );
};

CalendarMonths.displayName = 'CalendarMonths';
